// ── FCM Push Notifications via Web Push Protocol ─────────────────────────────
const VAPID_KEY  = 'BPnlC-o5fYjfmkZjTOlo4a6SKIO5OJpR1s7vMAPk58iXlplKhscCZRBkwzBqD7LKmZ9TkwYzS3vkijMmlUDxxsg';
const WORKER_URL = 'https://pxtracker-notify.csmariano.workers.dev';
const FIREBASE_API = 'https://firestore.googleapis.com/v1/projects/pxtracker-d6acd/databases/(default)/documents';

// ── Stable device ID ──────────────────────────────────────────────────────────
export function getDeviceId() {
  let id = localStorage.getItem('fcm_device_id');
  if (!id) { id = 'dev_' + Math.random().toString(36).slice(2) + Date.now().toString(36); localStorage.setItem('fcm_device_id', id); }
  return id;
}

// ── Register service worker ───────────────────────────────────────────────────
export async function initPush() {
  if (!('serviceWorker' in navigator) || !('PushManager' in window)) { console.warn('[FCM] Push not supported'); return false; }
  try {
    await navigator.serviceWorker.register('/sw.js', { scope: '/' });
    console.log('[FCM] SW registered');
    return true;
  } catch (e) { console.warn('[FCM] SW registration failed:', e); return false; }
}

// ── Subscribe to push (call after permission granted) ────────────────────────
async function subscribeToPush() {
  try {
    const swReg = await navigator.serviceWorker.ready;

    const existing = await swReg.pushManager.getSubscription();
    if (existing) {
      const stored = localStorage.getItem('fcm_subscription');
      if (stored) {
        console.log('[FCM] Already subscribed, reusing');
        return JSON.parse(stored);
      }
      await existing.unsubscribe();
    }

    const vapidKey = urlBase64ToUint8Array(VAPID_KEY);
    const subscription = await swReg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: vapidKey });
    const subStr = JSON.stringify(subscription);
    localStorage.setItem('fcm_subscription', subStr);
    console.log('[FCM] Subscribed:', subscription.endpoint.slice(0, 60) + '...');
    return subscription;
  } catch (e) {
    console.warn('[FCM] Subscribe failed:', e);
    return null;
  }
}

// ── Request permission + subscribe ───────────────────────────────────────────
export async function requestPushPermission() {
  if (typeof Notification === 'undefined') return false;
  let perm = Notification.permission;
  if (perm === 'default') perm = await Notification.requestPermission();
  if (perm !== 'granted') { console.warn('[FCM] Permission denied:', perm); return false; }
  return await subscribeToPush();
}

// ── Register device in Firestore ──────────────────────────────────────────────
export async function registerDeviceForShift(shiftCode) {
  if (!shiftCode) return;
  let subStr = localStorage.getItem('fcm_subscription');
  if (!subStr) {
    const sub = await subscribeToPush();
    if (!sub) { console.warn('[FCM] Could not subscribe'); return; }
    subStr = localStorage.getItem('fcm_subscription');
  }
  const deviceId = getDeviceId();
  const docPath = `${FIREBASE_API}/push_tokens/${shiftCode}/devices/${deviceId}`;
  try {
    const res = await fetch(docPath, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields: {
        subscription: { stringValue: subStr },
        deviceId:     { stringValue: deviceId },
        updatedAt:    { integerValue: String(Date.now()) },
      }}),
    });
    const json = await res.json();
    if (json.error) console.warn('[FCM] Firestore register error:', json.error);
    else console.log('[FCM] Registered device for shift:', shiftCode);
  } catch (e) { console.warn('[FCM] Register error:', e); }
}

// ── Unregister device — also unsubscribes the browser push subscription ───────
export async function unregisterDeviceFromShift(shiftCode) {
  if (!shiftCode) return;
  const deviceId = getDeviceId();
  try {
    // Delete from Firestore so no more pushes are sent to this device
    await fetch(`${FIREBASE_API}/push_tokens/${shiftCode}/devices/${deviceId}`, { method: 'DELETE' });
    // Also unsubscribe the browser-level push subscription so it truly stops
    if ('serviceWorker' in navigator) {
      const swReg = await navigator.serviceWorker.ready;
      const sub = await swReg.pushManager.getSubscription();
      if (sub) await sub.unsubscribe();
    }
    localStorage.removeItem('fcm_subscription');
    console.log('[FCM] Unregistered and unsubscribed from shift:', shiftCode);
  } catch (e) { console.warn('[FCM] Unregister error:', e); }
}

// ── Send push to all other devices in shift ───────────────────────────────────
export async function sendPush(shiftCode, title, body, pt = {}) {
  if (!shiftCode) return;
  try {
    const res = await fetch(`${FIREBASE_API}/push_tokens/${shiftCode}/devices`, { method: 'GET' });
    const json = await res.json();
    if (!json.documents?.length) { console.warn('[FCM] No devices registered for shift:', shiftCode); return; }
    const myId = getDeviceId();
    const subscriptions = json.documents
      .map(d => ({ deviceId: d.fields?.deviceId?.stringValue, subscription: d.fields?.subscription?.stringValue }))
      .filter(d => d.subscription && d.deviceId !== myId);
    if (!subscriptions.length) { console.log('[FCM] No other devices to notify'); return; }
    const workerRes = await fetch(WORKER_URL, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        subscriptions: subscriptions.map(d => d.subscription),
        title, message: body,
        data: { sender_id: myId, team: pt.team || '', ss: pt.subspec || '', svc: pt.code || '' },
      }),
    });
    const workerJson = await workerRes.json();
    console.log('[FCM] Worker response:', JSON.stringify(workerJson));
  } catch (e) { console.warn('[FCM] Send error:', e); }
}

// ── VAPID key converter ───────────────────────────────────────────────────────
function urlBase64ToUint8Array(b64) {
  const pad = '='.repeat((4 - b64.length % 4) % 4);
  return Uint8Array.from(atob((b64 + pad).replace(/-/g, '+').replace(/_/g, '/')), c => c.charCodeAt(0));
}

// ── Write notification filter prefs to cache (readable by sw.js) ──────────────
export async function syncPrefsToSW(prefs) {
  if (!('caches' in window)) return;
  try {
    const cache = await caches.open('pxtracker-prefs');
    await cache.put('/prefs', new Response(JSON.stringify(prefs), { headers: { 'Content-Type': 'application/json' } }));
  } catch (e) { console.warn('[FCM] Could not sync prefs to SW:', e); }
}
